import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Tabs,
  Tab,
  LinearProgress,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Badge
} from '@mui/material';
import {
  Psychology as PsychologyIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  AttachMoney as AttachMoneyIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Download as DownloadIcon,
  Settings as SettingsIcon,
  Insights as InsightsIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  Speed as SpeedIcon,
  BugReport as BugReportIcon,
  Lightbulb as LightbulbIcon,
  ExpandMore as ExpandMoreIcon,
  AutoAwesome as AutoAwesomeIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';

interface AIAnalysis {
  _id: string;
  name: string;
  description: string;
  project: string;
  analysisType: 'risk' | 'schedule' | 'cost' | 'resource' | 'quality' | 'comprehensive';
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress: number;
  results: {
    summary: string;
    insights: Array<{
      type: 'risk' | 'opportunity' | 'recommendation' | 'warning';
      title: string;
      description: string;
      impact: 'low' | 'medium' | 'high' | 'critical';
      confidence: number;
      category: string;
    }>;
    metrics: {
      riskScore: number;
      scheduleHealth: number;
      costVariance: number;
      resourceUtilization: number;
      qualityIndex: number;
    };
    predictions: {
      completionDate: string;
      finalCost: number;
      riskProbability: number;
      successProbability: number;
    };
  };
  parameters: {
    includeHistoricalData: boolean;
    analysisDepth: 'basic' | 'detailed' | 'comprehensive';
    confidenceThreshold: number;
    timeHorizon: number;
  };
  createdAt: string;
  updatedAt: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ai-analysis-tabpanel-${index}`}
      aria-labelledby={`ai-analysis-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const AIAnalysis: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  
  // Get planId from query parameters using useMemo to prevent infinite re-renders
  const planId = React.useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('planId');
  }, []);
  
  // Debug logging for URL parameters
  console.log('🔍 [AI Analysis] Component mounted/updated');
  console.log('🔍 [AI Analysis] Current URL:', window.location.href);
  console.log('🔍 [AI Analysis] URL pathname:', window.location.pathname);
  console.log('🔍 [AI Analysis] URL search params:', window.location.search);
  console.log('🔍 [AI Analysis] projectId from useParams:', projectId);
  console.log('🔍 [AI Analysis] planId from query params:', planId);
  console.log('🔍 [AI Analysis] Both projectId and planId available:', !!(projectId || planId));
  
  const [analyses, setAnalyses] = useState<AIAnalysis[]>([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState<AIAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [runningAnalysis, setRunningAnalysis] = useState<string | null>(null);
  const [newAnalysis, setNewAnalysis] = useState({
    name: '',
    description: '',
    analysisType: 'comprehensive' as const,
    parameters: {
      includeHistoricalData: true,
      analysisDepth: 'detailed' as const,
      confidenceThreshold: 0.8,
      timeHorizon: 30
    }
  });

  useEffect(() => {
    console.log('🔄 [AI Analysis] useEffect triggered');
    console.log('🔄 [AI Analysis] useEffect - projectId:', projectId);
    console.log('🔄 [AI Analysis] useEffect - planId:', planId);
    
    // Always fetch all analyses on component mount
    console.log('🔄 [AI Analysis] useEffect - calling fetchAnalyses');
    fetchAnalyses();
  }, []);

  const fetchAnalyses = async () => {
    console.log('🔄 [AI Analysis] Starting fetchAnalyses...');
    console.log('🔄 [AI Analysis] Current loading state:', loading);
    
    try {
      console.log('🔄 [AI Analysis] Setting loading to true');
      setLoading(true);
      setError(null);
      
      // Fetch all analyses without filtering by project/plan
      const url = '/ai-analysis';
      
      console.log('🔄 [AI Analysis] Final API URL:', url);
      console.log('🔄 [AI Analysis] Making API call to fetch all analyses...');
      
      const response = await api.get(url);
      console.log('✅ [AI Analysis] API call successful!');
      console.log('✅ [AI Analysis] Response status:', response.status);
      console.log('✅ [AI Analysis] Response data:', response.data);
      
      const analysesData = response.data.data || [];
      console.log('✅ [AI Analysis] Extracted analyses data:', analysesData);
      console.log('✅ [AI Analysis] Number of analyses found:', analysesData.length);
      
      setAnalyses(analysesData);
      console.log('✅ [AI Analysis] Analyses state updated');
      
      // Set the most recent analysis as selected
      if (analysesData.length > 0) {
        setSelectedAnalysis(analysesData[0]);
        console.log('✅ [AI Analysis] Selected first analysis:', analysesData[0].name);
      } else {
        console.log('⚠️ [AI Analysis] No analyses found to select');
      }
      
      console.log('✅ [AI Analysis] Setting loading to false');
      setLoading(false);
      console.log('✅ [AI Analysis] fetchAnalyses completed successfully');
    } catch (err: any) {
      console.error('❌ [AI Analysis] Error in fetchAnalyses:', err);
      console.error('❌ [AI Analysis] Error response:', err.response);
      console.error('❌ [AI Analysis] Error message:', err.message);
      console.error('❌ [AI Analysis] Error status:', err.response?.status);
      console.error('❌ [AI Analysis] Error data:', err.response?.data);
      
      const errorMessage = err.response?.data?.message || 'Failed to fetch AI analyses';
      console.error('❌ [AI Analysis] Setting error message:', errorMessage);
      setError(errorMessage);
      
      console.log('❌ [AI Analysis] Setting loading to false due to error');
      setLoading(false);
    }
  };

  const handleCreateAnalysis = async () => {
    try {
      const analysisData = {
        ...newAnalysis,
        ...(planId ? { plan: planId } : { project: projectId })
      };
      
      console.log('Creating AI analysis with data:', analysisData);
      const response = await api.post('/ai-analysis', analysisData);
      console.log('AI analysis created:', response.data);
      
      setCreateDialogOpen(false);
      setNewAnalysis({
        name: '',
        description: '',
        analysisType: 'comprehensive',
        parameters: {
          includeHistoricalData: true,
          analysisDepth: 'detailed',
          confidenceThreshold: 0.8,
          timeHorizon: 30
        }
      });
      
      fetchAnalyses();
    } catch (err: any) {
      console.error('Error creating AI analysis:', err);
      setError(err.response?.data?.message || 'Failed to create analysis');
    }
  };

  const handleRunAnalysis = async (analysisId: string) => {
    try {
      setRunningAnalysis(analysisId);
      await api.post(`/ai-analysis/${analysisId}/run`);
      
      // Poll for updates
      const pollInterval = setInterval(async () => {
        try {
          const response = await api.get(`/ai-analysis/${analysisId}`);
          const analysis = response.data.data;
          
          setAnalyses(prev => prev.map(a => a._id === analysisId ? analysis : a));
          
          if (selectedAnalysis?._id === analysisId) {
            setSelectedAnalysis(analysis);
          }
          
          if (analysis.status === 'completed' || analysis.status === 'failed') {
            clearInterval(pollInterval);
            setRunningAnalysis(null);
          }
        } catch (err) {
          clearInterval(pollInterval);
          setRunningAnalysis(null);
        }
      }, 2000);
      
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to run analysis');
      setRunningAnalysis(null);
    }
  };

  const handleStopAnalysis = async (analysisId: string) => {
    try {
      await api.post(`/ai-analysis/${analysisId}/stop`);
      setRunningAnalysis(null);
      fetchAnalyses();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to stop analysis');
    }
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'risk': return <WarningIcon color="error" />;
      case 'opportunity': return <TrendingUpIcon color="success" />;
      case 'recommendation': return <LightbulbIcon color="primary" />;
      case 'warning': return <BugReportIcon color="warning" />;
      default: return <InsightsIcon />;
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const renderOverview = () => {
    if (!selectedAnalysis) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary">
            Select an analysis to view results
          </Typography>
        </Box>
      );
    }

    const { results } = selectedAnalysis;
    if (!results || !results.metrics || !results.predictions) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary">
            No results available. Run the analysis to generate insights.
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        {/* Metrics Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 3, mb: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Risk Score
                  </Typography>
                  <Typography variant="h4">
                    {results.metrics.riskScore}/100
                  </Typography>
                </Box>
                <WarningIcon color="error" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Schedule Health
                  </Typography>
                  <Typography variant="h4">
                    {results.metrics.scheduleHealth}%
                  </Typography>
                </Box>
                <ScheduleIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Cost Variance
                  </Typography>
                  <Typography variant="h4">
                    {results.metrics.costVariance > 0 ? '+' : ''}{results.metrics.costVariance}%
                  </Typography>
                </Box>
                <AttachMoneyIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Resource Utilization
                  </Typography>
                  <Typography variant="h4">
                    {results.metrics.resourceUtilization}%
                  </Typography>
                </Box>
                <SpeedIcon color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Quality Index
                  </Typography>
                  <Typography variant="h4">
                    {results.metrics.qualityIndex}/10
                  </Typography>
                </Box>
                <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Summary */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Analysis Summary
            </Typography>
            <Typography variant="body1">
              {results.summary}
            </Typography>
          </CardContent>
        </Card>

        {/* Predictions */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              AI Predictions
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <Box>
                <Typography variant="subtitle2">Predicted Completion Date</Typography>
                <Typography variant="body1" gutterBottom>
                  {new Date(results.predictions.completionDate).toLocaleDateString()}
                </Typography>
                
                <Typography variant="subtitle2">Final Cost Estimate</Typography>
                <Typography variant="body1" gutterBottom>
                  ${results.predictions.finalCost.toLocaleString()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Risk Probability</Typography>
                <Typography variant="body1" gutterBottom>
                  {(results.predictions.riskProbability * 100).toFixed(1)}%
                </Typography>
                
                <Typography variant="subtitle2">Success Probability</Typography>
                <Typography variant="body1" gutterBottom>
                  {(results.predictions.successProbability * 100).toFixed(1)}%
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderInsights = () => {
    if (!selectedAnalysis?.results?.insights) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary">
            No insights available
          </Typography>
        </Box>
      );
    }

    const insights = selectedAnalysis.results.insights;
    const groupedInsights = insights.reduce((acc, insight) => {
      if (!acc[insight.category]) {
        acc[insight.category] = [];
      }
      acc[insight.category].push(insight);
      return acc;
    }, {} as Record<string, typeof insights>);

    return (
      <Box>
        {Object.entries(groupedInsights).map(([category, categoryInsights]) => (
          <Accordion key={category} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                {category} ({categoryInsights.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List>
                {categoryInsights.map((insight, index) => (
                  <ListItem key={index} divider>
                    <ListItemIcon>
                      {getInsightIcon(insight.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">
                            {insight.title}
                          </Typography>
                          <Chip
                            label={insight.impact}
                            size="small"
                            color={getImpactColor(insight.impact) as any}
                          />
                          <Chip
                            label={`${(insight.confidence * 100).toFixed(0)}% confidence`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={insight.description}
                    />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    );
  };

  const renderAnalysisList = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Analysis History
        </Typography>
        <Button
          variant="contained"
          startIcon={<AutoAwesomeIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          New Analysis
        </Button>
      </Box>
      
      <List>
        {analyses.length === 0 ? (
          <ListItem>
            <ListItemText
              primary="No analyses found"
              secondary="Create a new analysis to get started with AI-powered project insights."
            />
          </ListItem>
        ) : (
          analyses.map((analysis) => (
            <ListItem
              key={analysis._id}
              divider
              sx={{
                cursor: 'pointer',
                backgroundColor: selectedAnalysis?._id === analysis._id ? 'action.selected' : 'inherit',
                '&:hover': { backgroundColor: 'action.hover' }
              }}
              onClick={() => setSelectedAnalysis(analysis)}
            >
            <ListItemIcon>
              <Badge
                color={analysis.status === 'completed' ? 'success' : 
                       analysis.status === 'running' ? 'primary' : 
                       analysis.status === 'failed' ? 'error' : 'default'}
                variant="dot"
              >
                <AnalyticsIcon />
              </Badge>
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1">
                    {analysis.name}
                  </Typography>
                  <Chip
                    label={analysis.analysisType}
                    size="small"
                    variant="outlined"
                  />
                  {analysis.status === 'running' && (
                    <CircularProgress size={16} />
                  )}
                </Box>
              }
              secondary={
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    {analysis.description}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Created: {new Date(analysis.createdAt).toLocaleDateString()}
                  </Typography>
                  {analysis.status === 'running' && (
                    <LinearProgress
                      variant="determinate"
                      value={analysis.progress}
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>
              }
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              {analysis.status === 'completed' && (
                <Tooltip title="Run Again">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRunAnalysis(analysis._id);
                    }}
                    disabled={runningAnalysis === analysis._id}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              )}
              {analysis.status === 'pending' && (
                <Tooltip title="Run Analysis">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRunAnalysis(analysis._id);
                    }}
                    disabled={runningAnalysis === analysis._id}
                  >
                    <PlayArrowIcon />
                  </IconButton>
                </Tooltip>
              )}
              {analysis.status === 'running' && (
                <Tooltip title="Stop Analysis">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStopAnalysis(analysis._id);
                    }}
                  >
                    <StopIcon />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setAnchorEl(e.currentTarget);
                  setSelectedAnalysis(analysis);
                }}
              >
                <MoreVertIcon />
              </IconButton>
            </Box>
          </ListItem>
        ))
        )}
      </List>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading AI analyses...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            AI Project Analysis
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Intelligent insights and predictions for your project
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchAnalyses}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AutoAwesomeIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            New Analysis
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 2fr' }, gap: 3 }}>
        {/* Analysis List */}
        <Paper sx={{ p: 2, height: 'fit-content' }}>
          {renderAnalysisList()}
        </Paper>

        {/* Analysis Results */}
        <Paper>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
              <Tab label="Overview" />
              <Tab label="Insights" />
              <Tab label="Details" />
            </Tabs>
          </Box>

            <TabPanel value={tabValue} index={0}>
              {renderOverview()}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              {renderInsights()}
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              {selectedAnalysis && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Analysis Configuration
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2">Analysis Type</Typography>
                      <Typography variant="body2" gutterBottom>
                        {selectedAnalysis.analysisType}
                      </Typography>
                      
                      <Typography variant="subtitle2">Analysis Depth</Typography>
                      <Typography variant="body2" gutterBottom>
                        {selectedAnalysis.parameters?.analysisDepth || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2">Confidence Threshold</Typography>
                      <Typography variant="body2" gutterBottom>
                        {selectedAnalysis.parameters?.confidenceThreshold ? (selectedAnalysis.parameters.confidenceThreshold * 100).toFixed(0) + '%' : 'N/A'}
                      </Typography>
                      
                      <Typography variant="subtitle2">Time Horizon</Typography>
                      <Typography variant="body2" gutterBottom>
                        {selectedAnalysis.parameters?.timeHorizon ? selectedAnalysis.parameters.timeHorizon + ' days' : 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </TabPanel>
          </Paper>
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => {
          // Download report
          setAnchorEl(null);
        }}>
          <DownloadIcon sx={{ mr: 1 }} />
          Download Report
        </MenuItem>
        <MenuItem onClick={() => {
          // Settings
          setAnchorEl(null);
        }}>
          <SettingsIcon sx={{ mr: 1 }} />
          Settings
        </MenuItem>
      </Menu>

      {/* Create Analysis Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New AI Analysis</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Analysis Name"
              value={newAnalysis.name}
              onChange={(e) => setNewAnalysis(prev => ({ ...prev, name: e.target.value }))}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={newAnalysis.description}
              onChange={(e) => setNewAnalysis(prev => ({ ...prev, description: e.target.value }))}
              sx={{ mb: 2 }}
            />
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Analysis Type</InputLabel>
              <Select
                value={newAnalysis.analysisType}
                onChange={(e) => setNewAnalysis(prev => ({ ...prev, analysisType: e.target.value as any }))}
              >
                <MenuItem value="comprehensive">Comprehensive Analysis</MenuItem>
                <MenuItem value="risk">Risk Analysis</MenuItem>
                <MenuItem value="schedule">Schedule Analysis</MenuItem>
                <MenuItem value="cost">Cost Analysis</MenuItem>
                <MenuItem value="resource">Resource Analysis</MenuItem>
                <MenuItem value="quality">Quality Analysis</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Analysis Depth</InputLabel>
              <Select
                value={newAnalysis.parameters.analysisDepth}
                onChange={(e) => setNewAnalysis(prev => ({
                  ...prev,
                  parameters: { ...prev.parameters, analysisDepth: e.target.value as any }
                }))}
              >
                <MenuItem value="basic">Basic</MenuItem>
                <MenuItem value="detailed">Detailed</MenuItem>
                <MenuItem value="comprehensive">Comprehensive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateAnalysis}>
            Create & Run Analysis
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AIAnalysis;